import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './JoinModal.css';

const JoinModal = ({
  section,
  user,
  memberData,
  setMemberData,
  onClose,
  onSubmit,
  jerseySizes,
  equipmentByCategory,
  toggleJerseyRequest,
  toggleEquipmentRequest,
  updateEquipmentRequest,
  removeEquipmentRequest
}) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentStep, setCurrentStep] = useState(1);

  // Get section equipment keys from the section object (from database)
  const sectionEquipmentKeys = section.equipment ? Object.keys(section.equipment) : [];
  
  // Map section equipment keys to actual equipment names in your catalog
  const getMatchingEquipment = () => {
    const allEquipment = Object.values(equipmentByCategory || {}).flat();
    const matchedEquipment = [];
    
    sectionEquipmentKeys.forEach(key => {
      const keyLower = key.toLowerCase();
      
      const match = allEquipment.find(item => {
        const itemNameLower = item.name.toLowerCase();
        
        // Direct matches
        if (itemNameLower.includes(keyLower) || keyLower.includes(itemNameLower)) {
          return true;
        }
        
        // Special mappings
        const mappings = {
          'helmet': ['helmet', 'cycling helmet'],
          'gloves': ['gloves', 'cycling gloves'],
          'shorts': ['shorts', 'cycling shorts'],
          'waterBottle': ['water bottle'],
          'yogaMat': ['yoga mat'],
          'swimCap': ['swim cap'],
          'swimsuit': ['swimsuit'],
          'goggles': ['goggles', 'swimming goggles'],
          'boots': ['boots', 'football boots'],
          'shinGuards': ['shin guards'],
          'basketballShoes': ['basketball shoes'],
          'racket': ['racket', 'tennis racket', 'badminton racket', 'squash racket'],
          'jersey': ['jersey'],
          'shoes': ['shoes', 'running shoes', 'tennis shoes'],
          'gi': ['gi', 'uniform'],
          'belt': ['belt'],
          'balletShoes': ['ballet shoes'],
          'leotard': ['leotard'],
          'tights': ['tights']
        };
        
        const mappedNames = mappings[keyLower] || [keyLower];
        return mappedNames.some(name => itemNameLower.includes(name));
      });
      
      if (match && !matchedEquipment.find(m => m.id === match.id)) {
        matchedEquipment.push(match);
      }
    });
    
    return matchedEquipment;
  };

  const getEquipmentByCategoryFiltered = () => {
    const matchedEquipment = getMatchingEquipment();
    const grouped = matchedEquipment.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {});
    return grouped;
  };

  const filteredEquipmentByCategory = getEquipmentByCategoryFiltered();
  const categories = ['all', ...Object.keys(filteredEquipmentByCategory)];

  const getFilteredEquipment = () => {
    if (activeCategory === 'all') {
      return Object.values(filteredEquipmentByCategory).flat();
    }
    return filteredEquipmentByCategory[activeCategory] || [];
  };

  const filteredEquipment = getFilteredEquipment();

  const canProceed = () => {
    if (currentStep === 1) {
      return memberData.fullName && memberData.email && memberData.phone;
    }
    if (currentStep === 2) {
      return true;
    }
    if (currentStep === 3) {
      return true;
    }
    return true;
  };

  const totalCost = () => {
    const equipmentCost = memberData.equipmentRequests
      .filter(e => e.type === 'buy')
      .reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const jerseyCost = (memberData.jerseyRequest.need && memberData.jerseyRequest.type === 'buy') 
      ? (section.equipment?.jersey?.price || 15000) * memberData.jerseyRequest.quantity 
      : 0;
    return equipmentCost + jerseyCost;
  };

  return (
    <motion.div 
      className="modal-overlay-3d"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="modal-container-3d join-modal-container"
        initial={{ scale: 0.9, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 50 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header-3d">
          <div className="header-content">
            <div className="header-icon-3d">
              <span className="header-emoji">🏃</span>
            </div>
            <div className="header-text">
              <h2>Join {section.name}</h2>
              <span className="facility-name">Section Membership</span>
            </div>
          </div>
          <motion.button 
            className="close-btn-3d"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
          >
            <span className="close-icon">✕</span>
          </motion.button>
        </div>

        <div className="modal-body-3d">
          {/* Section Info Card */}
          <motion.div 
            className="facility-card-3d"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="facility-icon-3d">
              <span className="facility-emoji">{section.icon?.replace('fa-', '') === 'person-running' ? '🏃' : 
                section.icon?.replace('fa-', '') === 'person-biking' ? '🚴' :
                section.icon?.replace('fa-', '') === 'person-walking' ? '🧘' :
                section.icon?.replace('fa-', '') === 'table-tennis-paddle-ball' ? '🏓' :
                section.icon?.replace('fa-', '') === 'basketball' ? '🏀' :
                section.icon?.replace('fa-', '') === 'futbol' ? '⚽' :
                section.icon?.replace('fa-', '') === 'golf-ball-tee' ? '⛳' :
                section.icon?.replace('fa-', '') === 'hand-fist' ? '🥋' :
                section.icon?.replace('fa-', '') === 'chess' ? '♟️' :
                section.icon?.replace('fa-', '') === 'font' ? '🔤' :
                section.icon?.replace('fa-', '') === 'table' ? '🎱' :
                section.icon?.replace('fa-', '') === 'people-group' ? '👭' : '🏅'}</span>
            </div>
            <div className="facility-details">
              <h3>{section.name}</h3>
              <p>{section.instructor} • {section.schedule}</p>
              <div className="facility-badges">
                <span className="badge-3d sporting">
                  <span className="badge-icon">📍</span>
                  {section.location}
                </span>
                <span className="badge-3d approval">
                  <span className="badge-icon">👥</span>
                  {section.capacity - section.enrolled} spots left
                </span>
              </div>
            </div>
          </motion.div>

          {/* 3D Progress Steps */}
          <div className="steps-container-3d">
            <div className="steps-track">
              {[1, 2, 3].map((step) => (
                <React.Fragment key={step}>
                  <div className="step-wrapper">
                    <motion.div 
                      className={`step-indicator-3d ${currentStep >= step ? 'active' : ''}`}
                      animate={currentStep >= step ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="step-number">{step}</span>
                    </motion.div>
                    <span className={`step-label ${currentStep >= step ? 'active' : ''}`}>
                      {step === 1 && 'Personal Info'}
                      {step === 2 && 'Equipment'}
                      {step === 3 && 'Confirm'}
                    </span>
                  </div>
                  {step < 3 && (
                    <div className={`step-connector ${currentStep > step ? 'active' : ''}`}>
                      <div className="connector-line"></div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="step-content-3d">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Step 1: Personal Information */}
                {currentStep === 1 && (
                  <div className="step-panel">
                    <div className="panel-title">
                      <span className="title-emoji">👤</span>
                      <h3>Personal Information</h3>
                    </div>
                    
                    <div className="form-group-3d">
                      <label className="label-3d">
                        <span className="label-emoji">📝</span>
                        Full Name *
                      </label>
                      <div className="input-wrapper-3d">
                        <input 
                          type="text" 
                          className="input-3d"
                          value={memberData.fullName}
                          onChange={(e) => setMemberData({...memberData, fullName: e.target.value})}
                          placeholder="Enter your full name"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group-3d half">
                        <label className="label-3d">
                          <span className="label-emoji">📧</span>
                          Email *
                        </label>
                        <div className="input-wrapper-3d">
                          <input 
                            type="email" 
                            className="input-3d"
                            value={memberData.email}
                            onChange={(e) => setMemberData({...memberData, email: e.target.value})}
                            placeholder="your@email.com"
                          />
                        </div>
                      </div>

                      <div className="form-group-3d half">
                        <label className="label-3d">
                          <span className="label-emoji">📞</span>
                          Phone *
                        </label>
                        <div className="input-wrapper-3d">
                          <input 
                            type="tel" 
                            className="input-3d"
                            value={memberData.phone}
                            onChange={(e) => setMemberData({...memberData, phone: e.target.value})}
                            placeholder="08012345678"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group-3d half">
                        <label className="label-3d">
                          <span className="label-emoji">💬</span>
                          WhatsApp (Optional)
                        </label>
                        <div className="input-wrapper-3d">
                          <input 
                            type="tel" 
                            className="input-3d"
                            value={memberData.whatsApp}
                            onChange={(e) => setMemberData({...memberData, whatsApp: e.target.value})}
                            placeholder="08012345678"
                          />
                        </div>
                      </div>

                      <div className="form-group-3d half">
                        <label className="label-3d">
                          <span className="label-emoji">🎂</span>
                          Date of Birth
                        </label>
                        <div className="input-wrapper-3d">
                          <input 
                            type="date" 
                            className="input-3d"
                            value={memberData.age}
                            onChange={(e) => setMemberData({...memberData, age: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Equipment Selection */}
                {currentStep === 2 && (
                  <div className="step-panel">
                    <div className="panel-title">
                      <span className="title-emoji">🔧</span>
                      <h3>Equipment Selection</h3>
                      <span className="optional-badge">Optional</span>
                    </div>

                    {/* Jersey Request */}
                    {section.equipment?.jersey && (
                      <div className="form-section-3d">
                        <h4 className="section-subtitle">
                          <i className="fa-solid fa-shirt"></i> Jersey / Kit
                        </h4>
                        
                        <label className="checkbox-3d">
                          <input
                            type="checkbox"
                            checked={memberData.jerseyRequest.need}
                            onChange={toggleJerseyRequest}
                          />
                          <span className="checkbox-check"></span>
                          <span>I need a jersey/kit</span>
                        </label>

                        {memberData.jerseyRequest.need && (
                          <div className="jersey-options-3d">
                            <div className="form-row">
                              <div className="form-group-3d">
                                <label className="label-3d">Size</label>
                                <select
                                  className="select-3d"
                                  value={memberData.jerseyRequest.size}
                                  onChange={(e) => setMemberData({
                                    ...memberData,
                                    jerseyRequest: { ...memberData.jerseyRequest, size: e.target.value }
                                  })}
                                >
                                  <option value="">Select size</option>
                                  {(section.equipment.jersey.sizes || jerseySizes).map(size => (
                                    <option key={size} value={size}>{size}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="form-group-3d">
                                <label className="label-3d">Type</label>
                                <select
                                  className="select-3d"
                                  value={memberData.jerseyRequest.type}
                                  onChange={(e) => setMemberData({
                                    ...memberData,
                                    jerseyRequest: { ...memberData.jerseyRequest, type: e.target.value }
                                  })}
                                >
                                  <option value="borrow">Borrow (Free)</option>
                                  <option value="buy">Buy (₦{section.equipment.jersey.price || 15000})</option>
                                </select>
                              </div>

                              {memberData.jerseyRequest.type === 'buy' && (
                                <div className="form-group-3d">
                                  <label className="label-3d">Quantity</label>
                                  <input
                                    type="number"
                                    className="input-3d"
                                    min="1"
                                    max="5"
                                    value={memberData.jerseyRequest.quantity}
                                    onChange={(e) => setMemberData({
                                      ...memberData,
                                      jerseyRequest: { ...memberData.jerseyRequest, quantity: parseInt(e.target.value) }
                                    })}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Additional Equipment */}
                    <div className="form-section-3d">
                      <h4 className="section-subtitle">
                        <i className="fa-solid fa-kitchen-set"></i> Additional Equipment
                      </h4>

                      {filteredEquipment.length > 0 ? (
                        <>
                          {categories.length > 1 && (
                            <div className="equipment-category-tabs">
                              {categories.map(cat => (
                                <button
                                  key={cat}
                                  className={`category-tab-3d ${activeCategory === cat ? 'active' : ''}`}
                                  onClick={() => setActiveCategory(cat)}
                                >
                                  {cat === 'all' ? 'All Equipment' : cat}
                                </button>
                              ))}
                            </div>
                          )}

                          <div className="equipment-grid-3d">
                            {filteredEquipment.map(item => {
                              const isSelected = memberData.equipmentRequests.some(e => e.id === item.id);
                              return (
                                <motion.button
                                  key={item.id}
                                  className={`equipment-card-3d ${isSelected ? 'selected' : ''}`}
                                  onClick={() => toggleEquipmentRequest(item)}
                                  whileHover={{ y: -4, scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <div className="card-icon">
                                    <i className={item.icon}></i>
                                  </div>
                                  <span className="item-name">{item.name}</span>
                                  <span className="item-available">{item.available} left</span>
                                  {isSelected && (
                                    <motion.div 
                                      className="selected-check"
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                    >
                                      ✓
                                    </motion.div>
                                  )}
                                </motion.button>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <div className="no-equipment-3d">
                          <span className="no-equipment-icon">📦</span>
                          <p>No additional equipment available for this section.</p>
                        </div>
                      )}

                      {/* Selected Equipment Details */}
                      {memberData.equipmentRequests.length > 0 && (
                        <div className="selected-items-3d">
                          <h4>Selected Items ({memberData.equipmentRequests.length})</h4>
                          {memberData.equipmentRequests.map(item => (
                            <motion.div 
                              key={item.id}
                              className="selected-item-3d"
                              layout
                            >
                              <div className="item-info">
                                <i className={item.icon}></i>
                                <span className="item-name">{item.name}</span>
                              </div>
                              {item.sizes && item.sizes.length > 0 && (
                                <select
                                  className="size-select-3d"
                                  value={item.size}
                                  onChange={(e) => updateEquipmentRequest(item.id, 'size', e.target.value)}
                                >
                                  {item.sizes.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                  ))}
                                </select>
                              )}
                              <select
                                className="type-select-3d"
                                value={item.type}
                                onChange={(e) => updateEquipmentRequest(item.id, 'type', e.target.value)}
                              >
                                <option value="borrow">Borrow</option>
                                <option value="buy">Buy {item.price > 0 && `(₦${item.price})`}</option>
                              </select>
                              {item.type === 'buy' && (
                                <div className="quantity-controls">
                                  <button onClick={() => updateEquipmentRequest(item.id, 'quantity', item.quantity - 1)} disabled={item.quantity <= 1}>−</button>
                                  <span>{item.quantity}</span>
                                  <button onClick={() => updateEquipmentRequest(item.id, 'quantity', item.quantity + 1)}>+</button>
                                </div>
                              )}
                              <button 
                                className="remove-item-btn"
                                onClick={() => removeEquipmentRequest(item.id)}
                              >
                                ✕
                              </button>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    <div className="form-section-3d">
                      <h4 className="section-subtitle">
                        <i className="fa-solid fa-note-sticky"></i> Additional Notes
                      </h4>
                      <textarea
                        className="textarea-3d"
                        rows="3"
                        value={memberData.notes}
                        onChange={(e) => setMemberData({...memberData, notes: e.target.value})}
                        placeholder="Any special requirements or notes..."
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Confirmation */}
                {currentStep === 3 && (
                  <div className="step-panel">
                    <div className="panel-title">
                      <span className="title-emoji">✓</span>
                      <h3>Review & Confirm</h3>
                    </div>

                    <motion.div 
                      className="summary-card-3d"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <div className="summary-header">
                        <div className="summary-id">
                          <span className="id-label">Section</span>
                          <span className="id-value">{section.name}</span>
                        </div>
                        <div className="status-badge-3d pending">Pending Approval</div>
                      </div>

                      <div className="summary-grid">
                        <div className="summary-row">
                          <span className="summary-label">Requestor</span>
                          <span className="summary-value">{memberData.fullName}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Email</span>
                          <span className="summary-value">{memberData.email}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Phone</span>
                          <span className="summary-value">{memberData.phone}</span>
                        </div>
                        {memberData.whatsApp && (
                          <div className="summary-row">
                            <span className="summary-label">WhatsApp</span>
                            <span className="summary-value">{memberData.whatsApp}</span>
                          </div>
                        )}
                        
                        {memberData.jerseyRequest.need && (
                          <div className="summary-row">
                            <span className="summary-label">Jersey</span>
                            <span className="summary-value">
                              {memberData.jerseyRequest.size} • {memberData.jerseyRequest.type}
                              {memberData.jerseyRequest.type === 'buy' && ` (x${memberData.jerseyRequest.quantity})`}
                            </span>
                          </div>
                        )}

                        {memberData.equipmentRequests.length > 0 && (
                          <div className="summary-row equipment-summary">
                            <span className="summary-label">Equipment</span>
                            <div className="equipment-tags">
                              {memberData.equipmentRequests.map(e => (
                                <span key={e.id} className="equipment-tag">
                                  {e.name} {e.type === 'buy' && `(Buy x${e.quantity})`}
                                  {e.type === 'borrow' && '(Borrow)'}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {memberData.notes && (
                          <div className="summary-row notes-summary">
                            <span className="summary-label">Notes</span>
                            <span className="summary-value notes">{memberData.notes}</span>
                          </div>
                        )}
                      </div>

                      {/* <div className="price-breakdown">
                        <div className="price-row">
                          <span>Membership Fee</span>
                          <span className="free">FREE</span>
                        </div>
                        {memberData.jerseyRequest.need && memberData.jerseyRequest.type === 'buy' && (
                          <div className="price-row">
                            <span>Jersey</span>
                            <span>₦{((section.equipment?.jersey?.price || 15000) * memberData.jerseyRequest.quantity).toLocaleString()}</span>
                          </div>
                        )}
                        {memberData.equipmentRequests.filter(e => e.type === 'buy').map(item => (
                          <div key={item.id} className="price-row">
                            <span>{item.name} x{item.quantity}</span>
                            <span>₦{(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        ))}
                        <div className="price-row total">
                          <span>Total Cost</span>
                          <span className="total-amount">₦{totalCost().toLocaleString()}</span>
                        </div>
                      </div> */}
                    </motion.div>

                    <div className="terms-section">
                      <label className="checkbox-3d large">
                        <input type="checkbox" id="terms" />
                        <span className="checkbox-check"></span>
                        <span className="terms-text">
                          I confirm that I am a club member and agree to the section rules and code of conduct
                        </span>
                      </label>
                    </div>

                    <motion.div 
                      className="approval-notice-3d"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <span className="notice-icon">ℹ️</span>
                      <p>
                        <strong>Request will be sent to the Club President for approval.</strong>
                      </p>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer-3d">
            {currentStep > 1 && (
              <motion.button 
                className="btn-3d secondary"
                whileHover={{ x: -4 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                <span className="btn-icon">←</span>
                Back
              </motion.button>
            )}
            {currentStep < 3 ? (
              <motion.button 
                className="btn-3d primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={currentStep === 1 && !canProceed()}
              >
                Continue
                <span className="btn-icon">→</span>
              </motion.button>
            ) : (
              <motion.button 
                className="btn-3d primary submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onSubmit}
              >
                <span className="btn-icon">📤</span>
                Submit Application
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default JoinModal;